import { useMemo, useState } from 'react';
import { MapPinned, Plus, Search, Target, TrendingUp } from 'lucide-react';
import { useWorkspace, authHeaders } from '../../context/WorkspaceContext';
import { Alert, Badge, Button, EmptyState, FormField, MetricCard, PageHeader, SectionHeader, Skeleton } from '../../components/ui';
import { useToast } from '../../components/ui/ToastProvider';
import DataTable from '../../components/ui/DataTable';
import './dashboard.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';
const BLANK = { keyword: '', location: '', searchRank: '', mapsRank: '' };

const position = value => (Number(value) > 0 ? Number(value) : null);

function PositionCell({ value }) {
  const rank = position(value);
  if (!rank) return <span className="dt-support">Not recorded</span>;
  const tone = rank <= 3 ? 'success' : rank <= 10 ? 'primary' : 'warning';
  return <Badge tone={tone}>#{rank}</Badge>;
}

export default function RankPage() {
  const { workspace, loading, reload } = useWorkspace();
  const { notify } = useToast();
  const [form, setForm] = useState(BLANK);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [market, setMarket] = useState('all');

  const keywords = useMemo(() => workspace.keywords || [], [workspace.keywords]);
  const markets = useMemo(() => [...new Set(keywords.map(item => item.location).filter(Boolean))], [keywords]);
  const rows = market === 'all' ? keywords : keywords.filter(item => item.location === market);

  const best = keywords.map(item => position(item.maps_rank) || position(item.search_rank)).filter(Boolean);
  const average = best.length ? Math.round(best.reduce((sum, value) => sum + value, 0) / best.length) : null;
  const topThree = best.filter(value => value <= 3).length;
  const topTen = best.filter(value => value <= 10).length;

  const change = event => setForm(current => ({ ...current, [event.target.name]: event.target.value }));

  const submit = async event => {
    event.preventDefault();
    setBusy(true); setError('');
    try {
      const response = await fetch(`${API}/workspace/keywords`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(form) });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Could not add keyword.');
      setForm(BLANK);
      await reload();
      notify('Keyword added to tracking.');
    } catch (requestError) {
      setError(requestError.message);
      notify(requestError.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="dt-stack-4"><Skeleton height={80} /><Skeleton height={110} /><Skeleton height={280} /></div>;

  return (
    <div className="dt-stack-5">
      <PageHeader
        title="Rank Tracking"
        description="Monitor where your business appears in local search."
        actions={<Button href="#add-keyword"><Plus size={15} />Add keyword</Button>}
      />

      <div className="dt-metric-grid">
        <MetricCard label="Average position" icon={TrendingUp} value={average ?? '—'} footer={best.length ? `Across ${best.length} recorded ${best.length === 1 ? 'keyword' : 'keywords'}` : 'No positions recorded'} help="The average of the best position recorded for each keyword." />
        <MetricCard label="Top 3" icon={Target} value={topThree} footer="Keywords in the local pack" />
        <MetricCard label="Top 10" icon={Search} value={topTen} footer="Keywords on page one" />
        <MetricCard label="Tracked" icon={MapPinned} value={keywords.length} unit={workspace.limits?.rankChecks ? `/ ${workspace.limits.rankChecks} checks` : ''} footer="Keyword and market combinations" />
      </div>

      <section className="dt-stack-3" aria-label="Tracked keywords">
        <SectionHeader title="Tracked keywords" description="Positions you have recorded for each keyword and market." />
        <DataTable
          rows={rows}
          searchKeys={['keyword', 'location']}
          searchPlaceholder="Search keywords"
          initialSort={{ id: 'best', direction: 'asc' }}
          caption="Tracked keyword positions"
          filters={markets.length > 1 ? [{
            id: 'market', label: 'Filter by market', value: market, onChange: setMarket,
            options: [{ value: 'all', label: 'All markets' }, ...markets.map(item => ({ value: item, label: item }))],
          }] : []}
          empty={<EmptyState
            icon={MapPinned}
            title="No keywords tracked yet"
            description="Add the search terms customers use to find businesses like yours, then record where you appear today."
            actions={<Button href="#add-keyword">Add your first keyword</Button>}
          />}
          columns={[
            { id: 'keyword', header: 'Keyword', primary: true, render: row => row.keyword, sortValue: row => row.keyword.toLowerCase() },
            { id: 'location', header: 'Market', render: row => row.location || '—', sortValue: row => row.location || '' },
            { id: 'search', header: 'Search', render: row => <PositionCell value={row.search_rank} />, sortValue: row => position(row.search_rank) || 999 },
            { id: 'maps', header: 'Maps', render: row => <PositionCell value={row.maps_rank} />, sortValue: row => position(row.maps_rank) || 999 },
            { id: 'best', header: 'Best', render: row => { const value = position(row.maps_rank) || position(row.search_rank); return value ? `#${value}` : '—'; }, sortValue: row => position(row.maps_rank) || position(row.search_rank) || 999 },
            { id: 'updated', header: 'Last checked', render: row => new Date(row.updated_at).toLocaleDateString(), sortValue: row => new Date(row.updated_at).getTime() },
          ]}
          renderCard={row => (
            <>
              <strong className="dt-card-title">{row.keyword}</strong>
              <dl>
                <dt>Market</dt><dd>{row.location || '—'}</dd>
                <dt>Search</dt><dd><PositionCell value={row.search_rank} /></dd>
                <dt>Maps</dt><dd><PositionCell value={row.maps_rank} /></dd>
                <dt>Last checked</dt><dd>{new Date(row.updated_at).toLocaleDateString()}</dd>
              </dl>
            </>
          )}
        />
      </section>

      <section className="dt-card dt-stack-4" id="add-keyword" aria-label="Add keyword">
        <SectionHeader title="Add a keyword" description="Record the position you see today so you can compare it after your next change." />
        <form className="dt-stack-4" onSubmit={submit}>
          <div className="dt-field-grid">
            <FormField label="Keyword" id="rank-keyword" required><input id="rank-keyword" className="dt-input" name="keyword" value={form.keyword} onChange={change} placeholder="emergency dentist" required /></FormField>
            <FormField label="Market" id="rank-location"><input id="rank-location" className="dt-input" name="location" value={form.location} onChange={change} placeholder="Winter Park, FL" /></FormField>
            <FormField label="Search position" id="rank-search" help="Where you appear in standard Google results."><input id="rank-search" className="dt-input" type="number" min="1" max="200" name="searchRank" value={form.searchRank} onChange={change} /></FormField>
            <FormField label="Maps position" id="rank-maps" help="Where you appear in the Google Maps local pack."><input id="rank-maps" className="dt-input" type="number" min="1" max="200" name="mapsRank" value={form.mapsRank} onChange={change} /></FormField>
          </div>
          {error && <Alert tone="danger">{error}</Alert>}
          <div><Button type="submit" loading={busy}><Plus size={15} />Add keyword</Button></div>
        </form>
      </section>
    </div>
  );
}
